import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Alert, Chip, Divider, IconButton, Tooltip, Switch, Snackbar,
  LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Sms, Refresh, CheckCircle, Error as ErrorIcon, Save,
  PowerSettingsNew, Speed, Warning, Send, PhoneAndroid,
  Timeline, Shield, Replay, SignalCellularAlt,
} from '@mui/icons-material';
import { smsGatewayAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// ═══════════════════════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, sub }) => (
  <Card sx={{
    borderRadius: 2, border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20` },
  }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8', mt: 0.2 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Section = ({ icon, title, color, children, action }) => (
  <Box>
    <Box className="flex items-center justify-between mb-1.5">
      <Box className="flex items-center gap-1.5">
        {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', minWidth: 80 }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// TEMPLATE DEFINITIONS (static, for the Template Management table)
// ═══════════════════════════════════════════════════════════════

const SMS_TEMPLATES = [
  { name: 'KYC Credentials', type: 'OTP', settingKey: 'msg91_template_id_credentials', description: 'Login credentials sent on KYC approval' },
  { name: 'KYC Rejection', type: 'Alert', settingKey: 'msg91_template_id_rejection', description: 'Rejection notification for KYC' },
  { name: 'Policy Confirmation', type: 'Policy', settingKey: null, description: 'Policy creation confirmation' },
  { name: 'Claim Update', type: 'Alert', settingKey: null, description: 'Claim status update notification' },
  { name: 'Credit Disbursement', type: 'Credit', settingKey: null, description: 'Credit disbursement notification' },
  { name: 'Payment Receipt', type: 'Alert', settingKey: null, description: 'Premium payment receipt' },
];

const TEMPLATE_TYPE_COLORS = {
  OTP: { bg: '#dbeafe', color: '#2563eb' },
  Alert: { bg: '#fef3c7', color: '#d97706' },
  Policy: { bg: '#dcfce7', color: '#16a34a' },
  Credit: { bg: '#f3e8ff', color: '#7c3aed' },
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const SMSGatewayPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState({});

  // Data from API
  const [settings, setSettings] = useState([]);

  // Test SMS
  const [testMobile, setTestMobile] = useState('');
  const [testMessage, setTestMessage] = useState('This is a test SMS from MeenSure admin panel.');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Session tracking
  const [smsStats, setSmsStats] = useState({ sentToday: 0, failedToday: 0, pendingRetries: 0 });
  const [failedSMSLog, setFailedSMSLog] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Helpers to extract settings ──
  const getSetting = useCallback((key) => {
    const s = settings.find(s => s.key === key);
    return s ? s.value : '';
  }, [settings]);

  const isEnabled = useMemo(() => {
    return getSetting('sms_enabled') === 'true';
  }, [getSetting]);

  const activeProvider = useMemo(() => {
    return getSetting('sms_active_provider') || 'msg91';
  }, [getSetting]);

  const authKeyConfigured = useMemo(() => {
    const val = getSetting('msg91_auth_key');
    return Boolean(val && val.trim() && val !== '****');
  }, [getSetting]);

  const senderIdConfigured = useMemo(() => {
    const val = getSetting('msg91_sender_id');
    return Boolean(val && val.trim());
  }, [getSetting]);

  const canSend = isEnabled && authKeyConfigured;

  // ── Audit helper ──
  const addAudit = useCallback((action, detail) => {
    setAuditLog(prev => [
      { timestamp: new Date().toISOString(), action, detail },
      ...prev.slice(0, 29),
    ]);
  }, []);

  // ── Load data ──
  const loadSettings = useCallback(async () => {
    try {
      const res = await smsGatewayAPI.getSettings();
      setSettings(res.data || []);
    } catch (err) {
      console.error('Error loading SMS settings:', err);
      setSnackbar({ open: true, message: 'Failed to load SMS settings', severity: 'error' });
    }
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    await loadSettings();
    setLoading(false);
    setRefreshing(false);
  }, [loadSettings]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Handlers ──
  const handleToggle = async () => {
    try {
      setToggling(true);
      const newState = !isEnabled;
      await smsGatewayAPI.toggle(newState);
      setSnackbar({ open: true, message: `SMS service ${newState ? 'enabled' : 'disabled'} successfully`, severity: 'success' });
      addAudit('Service Toggled', `SMS service ${newState ? 'enabled' : 'disabled'}`);
      await loadSettings();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to toggle SMS service', severity: 'error' });
    } finally {
      setToggling(false);
    }
  };

  const handleSaveSetting = async (key, value, label) => {
    if (!value.trim()) {
      setSnackbar({ open: true, message: `Please enter a ${label}`, severity: 'error' });
      return;
    }
    try {
      setSaving(prev => ({ ...prev, [key]: true }));
      await smsGatewayAPI.updateSetting(key, value.trim());
      setSnackbar({ open: true, message: `${label} saved successfully!`, severity: 'success' });
      addAudit(`${label} Updated`, `${label} was updated`);
      await loadSettings();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || `Failed to save ${label}`, severity: 'error' });
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleTestSMS = async () => {
    if (!testMobile.trim() || testMobile.trim().length < 10) {
      setSnackbar({ open: true, message: 'Enter a valid 10-digit mobile number', severity: 'error' });
      return;
    }
    try {
      setTestSending(true);
      setTestResult(null);
      const res = await smsGatewayAPI.testSMS(testMobile.trim(), testMessage.trim());
      const result = res.data?.result || res.data;
      setTestResult(result);
      addAudit('Test SMS', `Sent to ${testMobile} — ${result?.success ? 'Success' : 'Failed'}`);

      if (result?.success) {
        setSmsStats(prev => ({ ...prev, sentToday: prev.sentToday + 1 }));
      } else {
        setSmsStats(prev => ({ ...prev, failedToday: prev.failedToday + 1 }));
        setFailedSMSLog(prev => [
          { timestamp: new Date().toISOString(), mobile: testMobile, reason: result?.error || result?.message || 'Unknown error' },
          ...prev.slice(0, 19),
        ]);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Test SMS failed';
      setTestResult({ success: false, error: msg });
      setSmsStats(prev => ({ ...prev, failedToday: prev.failedToday + 1 }));
      setFailedSMSLog(prev => [
        { timestamp: new Date().toISOString(), mobile: testMobile, reason: msg },
        ...prev.slice(0, 19),
      ]);
      addAudit('Test SMS', `Failed to ${testMobile}: ${msg}`);
    } finally {
      setTestSending(false);
    }
  };

  const handleRetry = async (entry) => {
    setSnackbar({ open: true, message: `Retrying SMS to ${entry.mobile}…`, severity: 'info' });
    try {
      const res = await smsGatewayAPI.testSMS(entry.mobile, 'Retry: ' + testMessage.trim());
      const result = res.data?.result || res.data;
      if (result?.success) {
        setFailedSMSLog(prev => prev.filter(e => e !== entry));
        setSmsStats(prev => ({ ...prev, sentToday: prev.sentToday + 1, failedToday: Math.max(0, prev.failedToday - 1) }));
        setSnackbar({ open: true, message: `Retry to ${entry.mobile} successful`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: `Retry to ${entry.mobile} failed`, severity: 'error' });
      }
      addAudit('SMS Retry', `${entry.mobile} — ${result?.success ? 'Success' : 'Failed'}`);
    } catch (err) {
      setSnackbar({ open: true, message: 'Retry failed', severity: 'error' });
    }
  };

  // Delivery rate
  const deliveryRate = useMemo(() => {
    const total = smsStats.sentToday + smsStats.failedToday;
    if (total === 0) return '—';
    return `${Math.round((smsStats.sentToday / total) * 100)}%`;
  }, [smsStats]);

  // Templates with status from settings
  const templates = useMemo(() => {
    return SMS_TEMPLATES.map(t => ({
      ...t,
      status: t.settingKey ? (getSetting(t.settingKey) ? 'Active' : 'Not Configured') : 'Built-in',
      templateId: t.settingKey ? getSetting(t.settingKey) : null,
    }));
  }, [getSetting]);

  // ── Loading state ──
  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #0891b2', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading messaging console…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ HEADER ══════════ */}
      <Box className="flex items-center justify-between mb-3">
        <Box className="flex items-center gap-2">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#0891b2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sms sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              Messaging Operations Console
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              SMS Gateway — Provider: <strong>{activeProvider.toUpperCase()}</strong>
            </Typography>
          </Box>
        </Box>
        <Box className="flex items-center gap-2">
          {/* Enable / Disable toggle */}
          <Box className="flex items-center gap-1.5" sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: isEnabled ? '#dcfce7' : '#fef2f2', border: `1px solid ${isEnabled ? '#22c55e' : '#fca5a5'}` }}>
            <PowerSettingsNew sx={{ fontSize: 16, color: isEnabled ? '#16a34a' : '#dc2626' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isEnabled ? '#16a34a' : '#dc2626' }}>
              {toggling ? 'Toggling…' : isEnabled ? 'Enabled' : 'Disabled'}
            </Typography>
            <Switch
              size="small" checked={isEnabled}
              onChange={handleToggle}
              disabled={toggling}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' },
              }}
            />
          </Box>
          <Tooltip title="Refresh"><IconButton size="small" onClick={() => loadAll(true)} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* ══════════ 2. PROVIDER STATUS PANEL ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden', border: `2px solid ${canSend ? '#22c55e' : '#fca5a5'}` }}>
        <Box sx={{ px: 2, py: 1.2, bgcolor: canSend ? '#f0fdf4' : '#fef2f2' }}>
          <Box className="flex items-center justify-between">
            <Box className="flex items-center gap-2">
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: canSend ? '#dcfce7' : '#fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {canSend ? <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} /> : <ErrorIcon sx={{ fontSize: 18, color: '#dc2626' }} />}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: canSend ? '#16a34a' : '#dc2626' }}>
                  {canSend ? 'Provider Active' : 'Provider Inactive'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                  {canSend ? `${activeProvider.toUpperCase()} is configured and ready to send` : !isEnabled ? 'SMS service is disabled' : 'API credentials not configured'}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-1.5">
              <Chip label={canSend ? 'Active' : 'Inactive'} size="small"
                sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: canSend ? '#dcfce7' : '#fef2f2', color: canSend ? '#16a34a' : '#dc2626' }} />
              <Chip label={activeProvider.toUpperCase()} size="small"
                sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#2563eb' }} />
            </Box>
          </Box>
        </Box>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
            <InfoRow label="Provider" value={activeProvider.toUpperCase()} />
            <InfoRow label="Auth Key" value={
              authKeyConfigured
                ? <Box className="flex items-center gap-1"><CheckCircle sx={{ fontSize: 11, color: '#16a34a' }} /><Typography sx={{ fontSize: '0.66rem', fontWeight: 600, fontFamily: 'monospace' }}>{getSetting('msg91_auth_key')}</Typography></Box>
                : <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
            } />
            <InfoRow label="Sender ID" value={
              senderIdConfigured
                ? <Box className="flex items-center gap-1"><CheckCircle sx={{ fontSize: 11, color: '#16a34a' }} /><Typography sx={{ fontSize: '0.66rem', fontWeight: 600, fontFamily: 'monospace' }}>{getSetting('msg91_sender_id')}</Typography></Box>
                : <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#d97706' }} />
            } />
            <InfoRow label="Route" value={getSetting('msg91_route') || '4 (Transactional)'} />
            <InfoRow label="API Balance" value={
              <Chip label="Check via MSG91 Dashboard" size="small"
                sx={{ height: 16, fontSize: '0.5rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }} />
            } />
            <InfoRow label="Delivery Rate" value={deliveryRate} />
          </Box>
        </CardContent>
      </Card>

      {/* ══════════ 3. SMS STATS KPI STRIP ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3">
        <KPICard icon={<Send />} label="Sent Today" color="#2563eb"
          value={smsStats.sentToday} sub="Session tracked" />
        <KPICard icon={<ErrorIcon />} label="Failed Today" color="#dc2626"
          value={smsStats.failedToday} sub="Session tracked" />
        <KPICard icon={<SignalCellularAlt />} label="Delivery %" color="#16a34a"
          value={deliveryRate} />
        <KPICard icon={<Replay />} label="Pending Retries" color="#d97706"
          value={failedSMSLog.length} />
      </Box>

      {/* ══════════ TWO-COLUMN LAYOUT ══════════ */}
      <Box className="flex gap-2.5" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>

        {/* ── LEFT COLUMN ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 4️⃣ Template Management Table */}
          <Section icon={<Sms />} title="Template Management" color="#0891b2">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Template Name</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Type</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>DLT Template ID</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((t, i) => {
                      const typeColor = TEMPLATE_TYPE_COLORS[t.type] || { bg: '#f1f5f9', color: '#64748b' };
                      return (
                        <TableRow key={i} hover>
                          <TableCell sx={{ py: 0.8 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e293b' }}>{t.name}</Typography>
                            <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8' }}>{t.description}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={t.type} size="small"
                              sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: typeColor.bg, color: typeColor.color }} />
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Typography sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: t.templateId ? '#1e293b' : '#cbd5e1' }}>
                              {t.templateId || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Chip label={t.status} size="small"
                              sx={{
                                height: 18, fontSize: '0.55rem', fontWeight: 700,
                                bgcolor: t.status === 'Active' ? '#dcfce7' : t.status === 'Built-in' ? '#dbeafe' : '#fef3c7',
                                color: t.status === 'Active' ? '#16a34a' : t.status === 'Built-in' ? '#2563eb' : '#d97706',
                              }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Section>

          {/* 5️⃣ Failed SMS Table */}
          <Section icon={<Warning />} title="Failed SMS Log" color="#dc2626"
            action={failedSMSLog.length > 0 && (
              <Button size="small" onClick={() => setFailedSMSLog([])}
                sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600, fontSize: '0.62rem' }}>Clear</Button>
            )}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              {failedSMSLog.length === 0 ? (
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#22c55e', opacity: 0.4, mb: 0.5 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8' }}>
                    No failed messages
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', mt: 0.3 }}>
                    All SMS delivery is running smoothly.
                  </Typography>
                </CardContent>
              ) : (
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Timestamp</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Mobile</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Reason</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedSMSLog.map((entry, i) => (
                        <TableRow key={i} sx={{ bgcolor: i === 0 ? '#fef2f2' : 'transparent' }}>
                          <TableCell sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: '#64748b', py: 0.8, whiteSpace: 'nowrap' }}>
                            {fmtDateTime(entry.timestamp)}
                          </TableCell>
                          <TableCell sx={{ py: 0.8 }}>
                            <Box className="flex items-center gap-0.5">
                              <PhoneAndroid sx={{ fontSize: 12, color: '#94a3b8' }} />
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, fontFamily: 'monospace', color: '#1e293b' }}>
                                {entry.mobile}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.62rem', color: '#dc2626', py: 0.8, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.reason}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.8 }}>
                            <Tooltip title="Retry SMS">
                              <IconButton size="small" onClick={() => handleRetry(entry)}
                                sx={{ color: '#2563eb', '&:hover': { bgcolor: '#dbeafe' } }}>
                                <Replay sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Section>
        </Box>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 6️⃣ Test SMS Section */}
          <Section icon={<Speed />} title="Test SMS" color="#7c3aed">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {!canSend && (
                  <Alert severity="warning" sx={{ borderRadius: 2, mb: 2, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    {!isEnabled ? 'Enable SMS service first.' : 'Configure API credentials first.'}
                  </Alert>
                )}

                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Mobile Number</Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="10-digit mobile number"
                  value={testMobile} onChange={(e) => setTestMobile(e.target.value)}
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                />

                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>Message</Typography>
                <TextField
                  fullWidth size="small" multiline rows={2}
                  placeholder="Test message body"
                  value={testMessage} onChange={(e) => setTestMessage(e.target.value)}
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
                />

                <Button fullWidth variant="contained"
                  startIcon={testSending ? null : <Send />}
                  onClick={handleTestSMS}
                  disabled={testSending || !canSend || !testMobile.trim()}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, fontSize: '0.75rem' }}>
                  {testSending ? 'Sending…' : 'Send Test SMS'}
                </Button>

                {testResult && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: testResult.success ? '#f0fdf4' : '#fef2f2' }}>
                    <Box className="flex items-center gap-1">
                      {testResult.success
                        ? <CheckCircle sx={{ fontSize: 16, color: '#16a34a' }} />
                        : <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />}
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: testResult.success ? '#16a34a' : '#dc2626' }}>
                        {testResult.success ? 'SMS Sent Successfully' : 'SMS Delivery Failed'}
                      </Typography>
                    </Box>
                    {testResult.error && (
                      <Typography sx={{ fontSize: '0.62rem', color: '#dc2626', mt: 0.5 }}>{testResult.error}</Typography>
                    )}
                    {testResult.message && (
                      <Typography sx={{ fontSize: '0.62rem', color: '#64748b', mt: 0.5 }}>{testResult.message}</Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Section>

          {/* Provider Configuration */}
          <Section icon={<Shield />} title="Provider Configuration" color="#0f172a">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Auth Key */}
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center justify-between mb-0.5">
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>MSG91 Auth Key</Typography>
                    {authKeyConfigured
                      ? <Chip label="Configured" size="small" icon={<CheckCircle sx={{ fontSize: 10 }} />} sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a', '& .MuiChip-icon': { color: '#16a34a' } }} />
                      : <Chip label="Missing" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
                    }
                  </Box>
                  {authKeyConfigured && (
                    <Typography sx={{ fontSize: '0.58rem', fontFamily: 'monospace', color: '#94a3b8', mb: 0.5 }}>{getSetting('msg91_auth_key')}</Typography>
                  )}
                  <AuthKeyInput
                    settingKey="msg91_auth_key"
                    label="Auth Key"
                    placeholder="Enter MSG91 Auth Key"
                    saving={saving}
                    onSave={handleSaveSetting}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Sender ID */}
                <Box sx={{ mb: 2 }}>
                  <Box className="flex items-center justify-between mb-0.5">
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569' }}>Sender ID</Typography>
                    {senderIdConfigured
                      ? <Chip label={getSetting('msg91_sender_id')} size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#dcfce7', color: '#16a34a' }} />
                      : <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#d97706' }} />
                    }
                  </Box>
                  <AuthKeyInput
                    settingKey="msg91_sender_id"
                    label="Sender ID"
                    placeholder="6-char sender (e.g. MEESUR)"
                    saving={saving}
                    onSave={handleSaveSetting}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* DLT Template IDs */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>DLT Template — Credentials</Typography>
                  <AuthKeyInput
                    settingKey="msg91_template_id_credentials"
                    label="Credentials Template ID"
                    placeholder="DLT template ID"
                    saving={saving}
                    onSave={handleSaveSetting}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>DLT Template — Rejection</Typography>
                  <AuthKeyInput
                    settingKey="msg91_template_id_rejection"
                    label="Rejection Template ID"
                    placeholder="DLT template ID"
                    saving={saving}
                    onSave={handleSaveSetting}
                  />
                </Box>
              </CardContent>
            </Card>
          </Section>
        </Box>
      </Box>

      {/* ══════════ 7. AUDIT LOG ══════════ */}
      <Box sx={{ mt: 2.5 }}>
        <Section icon={<Timeline />} title="Audit Log" color="#64748b"
          action={auditLog.length > 0 && (
            <Button size="small" onClick={() => setAuditLog([])} sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600, fontSize: '0.62rem' }}>Clear</Button>
          )}>
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Static info */}
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isEnabled ? '#16a34a' : '#dc2626', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>
                    SMS Service {isEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Current state</Typography>
                </Box>
              </Box>
              <Box className="flex items-start gap-2 mb-2">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb', mt: 0.5, flexShrink: 0 }} />
                <Box className="min-w-0 flex-1">
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>Active Provider: {activeProvider.toUpperCase()}</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    Auth Key: {authKeyConfigured ? 'Configured' : 'Not set'} &middot; Sender ID: {senderIdConfigured ? getSetting('msg91_sender_id') : 'Not set'}
                  </Typography>
                </Box>
              </Box>

              {/* Session audit entries */}
              {auditLog.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 1 }}>
                    Session Activity
                  </Typography>
                  {auditLog.map((entry, i) => (
                    <Box key={i} className="flex items-start gap-2 mb-1.5">
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#64748b', mt: 0.6, flexShrink: 0 }} />
                      <Box className="min-w-0 flex-1">
                        <Box className="flex items-center gap-1">
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#1e293b' }}>{entry.action}</Typography>
                          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', ml: 'auto' }}>{fmtTime(entry.timestamp)}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{entry.detail}</Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {auditLog.length === 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1', textAlign: 'center' }}>
                    No session activity yet. Actions will appear here as you interact.
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 1 }} />
              <Box className="flex items-center gap-1">
                <Shield sx={{ fontSize: 12, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.56rem', color: '#94a3b8' }}>
                  All SMS configuration changes are audit-logged.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Section>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// AUTH KEY INPUT — small reusable input + save button
// ═══════════════════════════════════════════════════════════════

const AuthKeyInput = ({ settingKey, label, placeholder, saving, onSave }) => {
  const [value, setValue] = useState('');

  return (
    <>
      <TextField
        fullWidth size="small"
        placeholder={placeholder}
        value={value} onChange={(e) => setValue(e.target.value)}
        sx={{ mb: 0.8, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.75rem' } }}
      />
      <Button fullWidth size="small" variant="contained"
        startIcon={saving[settingKey] ? null : <Save />}
        onClick={() => { onSave(settingKey, value, label); setValue(''); }}
        disabled={saving[settingKey] || !value.trim()}
        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.6, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, fontSize: '0.72rem' }}>
        {saving[settingKey] ? 'Saving…' : `Save ${label}`}
      </Button>
    </>
  );
};

export default SMSGatewayPage;
